import type { IFuseOptions } from 'fuse.js'
import Fuse from 'fuse.js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TABLE_STATE_KEYS, type TableSessionStore } from './table-session-store'
import { FUZZY_SEARCH_DEFAULTS } from './use-fuzzy-search'

/** Distinct, case-insensitively sorted labels pulled off every row. */
export function collectLabels<TRow>(
  rows: TRow[],
  pick: (row: TRow) => string[],
): string[] {
  const labels = new Set<string>()
  for (const row of rows) {
    for (const label of pick(row)) labels.add(label)
  }
  return Array.from(labels).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase()),
  )
}

/** Every admin table's filter set carries the free-text box. */
export interface BaseTableFilters {
  globalFilter: string
}

export interface UseFilteredTableStateOptions<
  TRow,
  TFilters extends BaseTableFilters,
> {
  rows: TRow[]
  /** When true, filter state and page size round-trip through `store`. */
  persist: boolean
  store: TableSessionStore
  /** Rows revealed per "load more" click, and the initial page size. */
  pageSize: number
  /** Filter values used when nothing is persisted. Must be a stable value. */
  defaultFilters: TFilters
  /** Fuse keys for the free-text box. Must be a stable value. */
  searchKeys: NonNullable<IFuseOptions<TRow>['keys']>
  /**
   * Whether a row matches an all-digits query. Such queries bypass fuzzy
   * search entirely so that pasting an external id lands on one row.
   * Must be a stable reference (declare it at module scope).
   */
  matchesExactId: (row: TRow, id: string) => boolean
  /**
   * Narrows the text-search results by the non-text filters. Must be a stable
   * reference (declare it at module scope).
   */
  applyFilters: (rows: TRow[], filters: TFilters) => TRow[]
}

export interface UseFilteredTableStateResult<
  TRow,
  TFilters extends BaseTableFilters,
> {
  filters: TFilters
  /**
   * Accepts a value or an updater, mirroring `useState` — TanStack Table's
   * `OnChangeFn` may hand either to the setters built on top of this.
   */
  setFilter: <K extends keyof TFilters>(
    key: K,
    value: TFilters[K] | ((prev: TFilters[K]) => TFilters[K]),
  ) => void
  filteredRows: TRow[]
  visibleCount: number
  loadMore: () => void
  /**
   * Passthrough for TanStack Table's `globalFilterFn` — the free-text search
   * has already been applied to `filteredRows`.
   */
  globalFilterFn: () => boolean
}

/**
 * Filter state, fuzzy search, session persistence, and "load more" pagination
 * for an admin table.
 *
 * Domain hooks wrap this to name their own filters (see `useTableFilters` for
 * the problems table and `useSDTableFilters` for system design); what lives
 * here is everything those two would otherwise hold byte-for-byte in common.
 */
export function useFilteredTableState<TRow, TFilters extends BaseTableFilters>({
  rows,
  persist,
  store,
  pageSize,
  defaultFilters,
  searchKeys,
  matchesExactId,
  applyFilters,
}: UseFilteredTableStateOptions<TRow, TFilters>): UseFilteredTableStateResult<
  TRow,
  TFilters
> {
  // Lazy initializers only — session storage is read once, at mount.
  const [filters, setFilters] = useState<TFilters>(() =>
    persist
      ? store.getValue(TABLE_STATE_KEYS.FILTERS, defaultFilters)
      : defaultFilters,
  )
  const [visibleCount, setVisibleCount] = useState(() =>
    persist
      ? store.getValue(TABLE_STATE_KEYS.VISIBLE_COUNT, pageSize)
      : pageSize,
  )

  const setFilter = useCallback(
    <K extends keyof TFilters>(
      key: K,
      value: TFilters[K] | ((prev: TFilters[K]) => TFilters[K]),
    ) => {
      setFilters((prev) => ({
        ...prev,
        [key]:
          typeof value === 'function'
            ? (value as (prev: TFilters[K]) => TFilters[K])(prev[key])
            : value,
      }))
    },
    [],
  )

  // Track if this is the initial mount so persisted pagination survives it
  const isInitialMount = useRef(true)

  // Any filter change sends the reader back to the first page
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    setVisibleCount(pageSize)
  }, [filters, pageSize])

  useEffect(() => {
    if (!persist) return
    store.setValue(TABLE_STATE_KEYS.FILTERS, filters)
  }, [persist, store, filters])

  useEffect(() => {
    if (!persist) return
    store.setValue(TABLE_STATE_KEYS.VISIBLE_COUNT, visibleCount)
  }, [persist, store, visibleCount])

  const fuse = useMemo(
    () => new Fuse(rows, { ...FUZZY_SEARCH_DEFAULTS, keys: searchKeys }),
    [rows, searchKeys],
  )

  const filteredRows = useMemo<TRow[]>(() => {
    let filtered = rows

    const searchValue = filters.globalFilter.trim()
    if (searchValue) {
      if (/^\d+$/.test(searchValue)) {
        filtered = filtered.filter((row) => matchesExactId(row, searchValue))
      } else {
        const matches = new Set(fuse.search(searchValue).map((r) => r.item))
        filtered = filtered.filter((row) => matches.has(row))
      }
    }

    return applyFilters(filtered, filters)
  }, [rows, filters, fuse, matchesExactId, applyFilters])

  const globalFilterFn = useCallback(() => true, [])

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => prev + pageSize)
  }, [pageSize])

  return {
    filters,
    setFilter,
    filteredRows,
    visibleCount,
    loadMore,
    globalFilterFn,
  }
}
